import { NavLink, Route, Routes, useParams, Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/api";
import { PageLoader, Progress } from "@/components/ui";
import { StatusBadge } from "@/components/badges";
import { money, pct } from "@/lib/format";
import { Overview } from "./project/Overview";
import { Schedule } from "./project/Schedule";
import { GanttView } from "./project/GanttView";
import { Team } from "./project/Team";
import { Files } from "./project/Files";
import { ProjectSettings } from "./project/ProjectSettings";
import { ProjectApprovals } from "./project/ProjectApprovals";

export interface ProjectBundle {
  project: any;
  phases: { id: string; name: string; seq: number; color: string | null }[];
  members: any[];
  access: {
    isMember: boolean; isOwner: boolean; isManager: boolean; canViewAll: boolean;
    canManageActivities: boolean; canDecideApprovals: boolean; projectRole: string | null;
  };
}

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["project", id],
    queryFn: () => api.get<ProjectBundle>(`/projects/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <PageLoader />;
  if (error || !data) return <div className="card p-8 text-center text-sm text-ink-soft">Project not found or you don't have access. <Link to="/projects" className="text-brand-600">Back to projects</Link></div>;

  const p = data.project;
  const tabs = [
    { to: "", label: "Overview", end: true },
    { to: "schedule", label: "Schedule" },
    { to: "gantt", label: "Gantt" },
    { to: "approvals", label: "Approvals" },
    { to: "team", label: "Team" },
    { to: "files", label: "Files" },
    ...(data.access.isManager || data.access.isOwner || data.access.canViewAll ? [{ to: "settings", label: "Settings" }] : []),
  ];

  return (
    <>
      <Link to="/projects" className="mb-3 inline-flex items-center gap-1.5 text-xs text-ink-faint hover:text-brand-600">
        <ArrowLeft size={13} /> All projects
      </Link>
      <div className="card mb-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-serif text-2xl font-bold">{p.name}</h1>
              <StatusBadge value={p.status} />
            </div>
            {p.description && <p className="mt-1 max-w-2xl text-sm text-ink-soft">{p.description}</p>}
            <p className="mt-1 text-xs text-ink-faint">
              {p.code && <>{p.code} · </>}Owner: {p.ownerName || "—"} · Manager: {p.managerName || "—"}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div><p className="text-lg font-semibold">{pct(p.overallPercent)}</p><p className="text-[11px] text-ink-faint">Complete</p></div>
            <div><p className="text-lg font-semibold">{p.totalActivities}</p><p className="text-[11px] text-ink-faint">Activities</p></div>
            <div><p className="text-lg font-semibold tabular-nums">{money(p.actualCost, p.currency)}</p><p className="text-[11px] text-ink-faint">of {money(p.plannedBudget, p.currency)}</p></div>
          </div>
        </div>
        <Progress value={p.overallPercent} className="mt-3" />
      </div>

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-line">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end}
            className={({ isActive }) => clsx(
              "whitespace-nowrap border-b-2 px-3.5 py-2 text-sm font-medium transition-colors",
              isActive ? "border-brand-600 text-brand-700 dark:text-brand-400" : "border-transparent text-ink-soft hover:text-ink",
            )}>
            {t.label}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route index element={<Overview bundle={data} />} />
        <Route path="schedule" element={<Schedule bundle={data} />} />
        <Route path="gantt" element={<GanttView bundle={data} />} />
        <Route path="approvals" element={<ProjectApprovals bundle={data} />} />
        <Route path="team" element={<Team bundle={data} />} />
        <Route path="files" element={<Files bundle={data} />} />
        <Route path="settings" element={<ProjectSettings bundle={data} />} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
    </>
  );
}
